import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload, FileSpreadsheet, CheckCircle, AlertCircle } from "lucide-react";
import API from "@/lib/api";
import { ENTITY_CONFIG } from "@/lib/entityConfig";

export default function UploadDialog({ open, entityType, onClose, onSuccess }) {
  const config = ENTITY_CONFIG[entityType];
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await API.post(`/${config.apiPath}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult({ success: true, count: res.data.count });
      onSuccess?.();
    } catch (err) {
      setResult({ success: false, error: err.response?.data?.detail || "Upload failed" });
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    onClose();
  };

  if (!config) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="bg-card border-border" data-testid="upload-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg">Upload {config.label}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Upload a CSV or Excel (.xlsx) file. Column names should match the field names below.
          </p>
          <div className="text-xs font-data bg-secondary/50 p-3 rounded-sm border border-border">
            <p className="font-semibold text-muted-foreground mb-1 uppercase tracking-wider">Expected columns:</p>
            <p className="text-foreground/80">{config.fields.map((f) => f.key).join(", ")}</p>
          </div>
          <div
            className="border-2 border-dashed border-border rounded-sm p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
            onClick={() => fileRef.current?.click()}
            data-testid="file-dropzone"
          >
            {file ? (
              <div className="flex items-center justify-center gap-2">
                <FileSpreadsheet size={20} className="text-primary" />
                <span className="text-sm font-medium">{file.name}</span>
                <span className="text-xs text-muted-foreground">({(file.size / 1024).toFixed(1)} KB)</span>
              </div>
            ) : (
              <div>
                <Upload size={28} className="mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">Click to select CSV or XLSX file</p>
              </div>
            )}
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              className="hidden"
              onChange={(e) => { setFile(e.target.files[0]); setResult(null); }}
              data-testid="file-input"
            />
          </div>
          {result && (
            <div
              className={`flex items-center gap-2 p-3 rounded-sm text-sm font-data ${
                result.success ? "bg-emerald-500/10 text-emerald-500" : "bg-red-500/10 text-red-500"
              }`}
              data-testid="upload-result"
            >
              {result.success ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
              {result.success ? `Successfully uploaded ${result.count} records` : result.error}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={handleClose} data-testid="upload-cancel">Close</Button>
          <Button onClick={handleUpload} disabled={!file || uploading} data-testid="upload-submit">
            {uploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
